import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/index'
import LandingPage from '@/pages/LandingPage'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import HomePage from '@/pages/HomePage'
import JoinOrgRequestPage from '@/pages/JoinOrgRequestPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage'
import TermsOfServicePage from '@/pages/TermsOfServicePage'
// import TPMTermsOfServicePage from '@/pages/TPMTermsOfServicePage'
import UserAccountForm from '@/components/UserAccountForm'
import OrgAdmin from '@/components/OrgAdmin'
import EmptyRouterView from '@/components/EmptyRouterView'

Vue.use(Router)

function setTitle (to, from, next) {
  // set page title
  // console.log(to)
  if (to.matched.length > 0) {
    if (to.meta && to.meta.title) {
      document.title = to.meta.title
    }
  }

  next()
}

function mustBeAuthenticated (to, from, next) {
  if (to.matched.some(route => route.meta.mustBeAuthenticated)) {
    if (store.getters['auth/isloggedin']) {
      next()
    } else {
      store.dispatch('auth/authenticate').then(() => {
        next()
      }).catch(() => {
        router.push({ name: 'login' })
      })
    }
  } else {
    next()
  }
}

function mustBeOrgAdmin (to, from, next) {
  if (to.matched.some(route => route.meta.mustBeOrgAdmin)) {
    if (store.getters['auth/isorgadmin']) {
      next()
    } else {
      router.push({ name: 'home' })
    }
  } else {
    next()
  }
}

function mustNotBeAuthenticated (to, from, next) {
  if (to.matched.some(route => route.meta.mustNotBeAuthenticated)) {
    if (store.getters['auth/isloggedin']) {
      // ex:
      //  instead of allowing a logged-in user to re-login,
      //  just redirect them to their account home
      router.push({ name: 'home' })
    } else {
      store.dispatch('auth/authenticate').then(() => {
        router.push({ name: 'home' })
      }).catch(() => {
        next()
      })
    }
  } else {
    next()
  }
}

const router = new Router({
  base: process.env.BASE_URL,
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'landing',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'MyStaffPro'
      },
      component: LandingPage
    },
    {
      path: '/login',
      name: 'login',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'Login'
      },
      component: LoginPage
    },
    {
      path: '/privacy',
      name: 'privacy',
      meta: {
        title: 'Privacy Policy'
      },
      component: PrivacyPolicyPage
    },
    {
      path: '/terms',
      name: 'terms',
      meta: {
        title: 'Terms of Service'
      },
      component: TermsOfServicePage
    },
    {
      path: '/signup',
      name: 'signup',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'Signup'
      },
      component: SignupPage
    },
    {
      path: '/activate/:token',
      name: 'activate',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'Activate'
      },
      component: EmptyRouterView,
      beforeEnter: (to, from, next) => {
        // should redirect on success
        store.dispatch('auth/activate', to.params.token).catch(() => {
          // something went wrong
          router.push({ name: 'login' })
        })
      }
    },
    {
      path: '/join-organization-request/:token',
      name: 'joinOrgRequest',
      meta: {
        title: 'Join Organization Request'
      },
      component: JoinOrgRequestPage
    },
    {
      path: '/reset',
      name: 'forgotpassword',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'Forgot Password'
      },
      component: ForgotPasswordPage
    },
    {
      path: '/reset/:token',
      name: 'reset',
      meta: {
        mustNotBeAuthenticated: true,
        title: 'Reset Password'
      },
      component: ResetPasswordPage
    },
    {
      path: '/home',
      name: 'home',
      meta: {
        mustBeAuthenticated: true,
        title: 'Home'
      },
      component: HomePage
    },
    {
      path: '/account',
      name: 'account',
      meta: {
        mustBeAuthenticated: true,
        title: 'My Account'
      },
      component: UserAccountForm
    },
    {
      path: '/organization',
      name: 'organization',
      meta: {
        mustBeAuthenticated: true,
        mustBeOrgAdmin: true,
        title: 'Organization Admin'
      },
      component: OrgAdmin
    },
    {
      path: '/apps',
      component: EmptyRouterView,
      meta: {
        mustBeAuthenticated: true // applies to all children
      },
      children: [
        {
          path: 'tpm',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'tpm',
            meta: {
              title: 'TPM Performance Measurement'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/TPM/App')
          }, {
            path: 'admin',
            name: 'tpmadmin',
            meta: {
              title: 'TPM Performance Measurement: Admin'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/TPM/Admin')
          }]
        },
        {
          path: 'checklist',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'checklist',
            meta: {
              title: 'New Branch Checklist'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/CHECKLIST/App')
          }]
        },
        {
          path: 'project-checklist',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'projchklst',
            meta: {
              title: 'Project Checklist'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/PROJCHKLST/App')
          }]
        },
        {
          path: 'files',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'files',
            meta: {
              title: 'File Upload & Download'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/FILES/App')
          }]
        },
        {
          path: 'monitor',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'monitor',
            meta: {
              title: 'Processing Monitor'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/MONITOR/App')
          }]
        },
        {
          path: 'crm',
          component: EmptyRouterView,
          children: [{
            path: '',
            name: 'crm',
            meta: {
              title: 'PEI CRM'
            },
            // load it async (see: https://dev.to/lobo_tuerto/dynamic-and-async-components-made-easy-with-vuejs-579j)
            component: () => import('@/components/apps/CRM/App')
          }]
        }
      ]
    }
  ]
})

router.beforeEach(setTitle)
router.beforeEach(mustBeAuthenticated)
router.beforeEach(mustNotBeAuthenticated)
router.beforeEach(mustBeOrgAdmin)

export default router
